const { MARKETING_CAMPAIGNS } = require("../data/marketing-campaigns")
const {
  getBrowserSessionStorage,
  processCampaignAttribution,
  readStoredAttribution,
  removeStoredAttribution,
  replaceTrackingUrl,
} = require("./campaign-attribution")
const {
  deleteKnownGaCookies,
  denyFutureMeasurement,
  loadCloudflareWebAnalytics,
  loadGoogleAnalytics,
} = require("./analytics-providers")
const {
  LEGACY_CONSENT_KEY,
  MEASUREMENT_ACTIVATION_GUARD_KEY,
  MEASUREMENT_NOTICE_KEY,
  MEASUREMENT_PREFERENCE_KEY,
  getMeasurementSnapshot,
  isMeasurementEnabled,
  storeMeasurementPreference,
} = require("./measurement-preferences")
const { dispatchPageView } = require("./analytics")

const MEASUREMENT_STATE_EVENT = "kalpilli:measurement-state-change"
const STORAGE_SYNC_MARKER = "__KALPILLI_MEASUREMENT_STORAGE_SYNC_BOUND__"
const RELOAD_MARKER = "__KALPILLI_MEASUREMENT_RELOAD_SCHEDULED__"
const PREFERENCE_CHANGE_MARKER = "__KALPILLI_MEASUREMENT_PREFERENCE_CHANGE__"
const RELEVANT_STORAGE_KEYS = new Set([
  MEASUREMENT_PREFERENCE_KEY,
  MEASUREMENT_NOTICE_KEY,
  MEASUREMENT_ACTIVATION_GUARD_KEY,
  LEGACY_CONSENT_KEY,
])

function initializeProviders({ target, document, config }) {
  if (!config || config.loadersDisabled)
    return { ga4: false, cloudflare: false }
  return {
    ga4: loadGoogleAnalytics(target, document, config.gaMeasurementId),
    cloudflare: loadCloudflareWebAnalytics(
      target,
      document,
      config.cloudflareToken
    ),
  }
}

function getCurrentMeasurementSnapshot(target, suppliedStorage) {
  const snapshot = getMeasurementSnapshot(target, suppliedStorage)
  if (
    !snapshot.dntActive &&
    target &&
    target.__KALPILLI_MEASUREMENT_EPHEMERAL_DISABLED__
  ) {
    return { ...snapshot, state: "disabled" }
  }
  return snapshot
}

function notifyMeasurementState(target, snapshot, source = "runtime") {
  try {
    if (
      !target ||
      !snapshot ||
      typeof target.dispatchEvent !== "function" ||
      typeof target.CustomEvent !== "function"
    ) {
      return false
    }
    target.dispatchEvent(
      new target.CustomEvent(MEASUREMENT_STATE_EVENT, {
        detail: {
          source,
          state: snapshot.state,
          preference: snapshot.preference,
          noticeDismissed: snapshot.noticeDismissed,
          dntActive: snapshot.dntActive,
        },
      })
    )
    return true
  } catch (_) {
    return false
  }
}

function scheduleReloadOnce(target) {
  if (!target || target[RELOAD_MARKER]) {
    return Boolean(target && target[RELOAD_MARKER])
  }
  try {
    target[RELOAD_MARKER] = true
    target.location.reload()
    return true
  } catch (_) {
    delete target[RELOAD_MARKER]
    return false
  }
}

function revokeCurrentDocument({ target, document, sessionStorage, state }) {
  denyFutureMeasurement(target)
  target.__KALPILLI_MEASUREMENT_STATE__ = state
  removeStoredAttribution(sessionStorage)
  deleteKnownGaCookies(document, target.location && target.location.hostname)
}

function handleMeasurementStorageEvent(event, options = {}) {
  const target =
    options.target || (typeof window !== "undefined" ? window : undefined)
  if (!target) return { handled: false, reloadScheduled: false }

  const key = event ? event.key : undefined
  if (key !== null && !RELEVANT_STORAGE_KEYS.has(key)) {
    return { handled: false, reloadScheduled: false }
  }

  const snapshot = getCurrentMeasurementSnapshot(target, options.localStorage)
  if (
    event &&
    event.storageArea &&
    snapshot.storage &&
    event.storageArea !== snapshot.storage
  ) {
    return { handled: false, reloadScheduled: false }
  }

  const previousState = target.__KALPILLI_MEASUREMENT_STATE__
  const wasEnabled = isMeasurementEnabled(previousState)
  const enabled = isMeasurementEnabled(snapshot.state)
  const sessionStorage =
    options.sessionStorage === undefined
      ? getBrowserSessionStorage(target)
      : options.sessionStorage
  const document = options.document || target.document

  if (!enabled) {
    revokeCurrentDocument({
      target,
      document,
      sessionStorage,
      state: snapshot.state,
    })
    notifyMeasurementState(target, snapshot, "storage")
    return { handled: true, reloadScheduled: false, snapshot }
  }

  if (!wasEnabled) {
    // Do not activate providers in-place. The activation guard has disappeared
    // and the final snapshot is enabled, so a fresh document may load them once.
    notifyMeasurementState(target, snapshot, "storage")
    return {
      handled: true,
      reloadScheduled: scheduleReloadOnce(target),
      snapshot,
    }
  }

  target.__KALPILLI_MEASUREMENT_STATE__ = snapshot.state
  notifyMeasurementState(target, snapshot, "storage")
  return { handled: true, reloadScheduled: false, snapshot }
}

function bindMeasurementStorageSync(options = {}) {
  const target =
    options.target || (typeof window !== "undefined" ? window : undefined)
  if (!target || typeof target.addEventListener !== "function") return false
  if (target[STORAGE_SYNC_MARKER]) return true

  try {
    const listener = event => handleMeasurementStorageEvent(event, options)
    target.addEventListener("storage", listener)
    target[STORAGE_SYNC_MARKER] = listener
    return true
  } catch (_) {
    delete target[STORAGE_SYNC_MARKER]
    return false
  }
}

function initializeMeasurement(options = {}) {
  const target =
    options.target || (typeof window !== "undefined" ? window : undefined)
  const document = options.document || (target && target.document)
  if (!target || !document) return null
  const registry = options.registry || MARKETING_CAMPAIGNS
  const now = options.now === undefined ? Date.now() : options.now
  const storage =
    options.sessionStorage === undefined
      ? getBrowserSessionStorage(target)
      : options.sessionStorage
  const incomingSearch = target.location ? target.location.search : ""
  const urlCleanup = replaceTrackingUrl(target, {
    navigate: options.navigate,
  })
  if (!urlCleanup.cleaned) {
    target.__KALPILLI_MEASUREMENT_EPHEMERAL_DISABLED__ = true
    target.__KALPILLI_MEASUREMENT_URL_CLEANUP_FAILED__ = true
  }
  const snapshot = getCurrentMeasurementSnapshot(target, options.localStorage)
  const enabled = isMeasurementEnabled(snapshot.state)
  target.__KALPILLI_MEASUREMENT_STATE__ = snapshot.state
  const attribution = processCampaignAttribution({
    search: incomingSearch,
    storage,
    registry,
    now,
    measurementEnabled: enabled,
  })
  if (!enabled) {
    denyFutureMeasurement(target)
    target.__KALPILLI_MEASUREMENT_STATE__ = snapshot.state
  }
  const providerInitializer = options.providerInitializer || initializeProviders
  const providerState = enabled
    ? providerInitializer({
        target,
        document,
        config: options.config || target.__KALPILLI_MEASUREMENT_CONFIG__ || {},
        attribution,
      })
    : { ga4: false, cloudflare: false }
  return {
    snapshot,
    attribution,
    urlCleaned: urlCleanup.cleaned,
    urlCleanup,
    providerState,
  }
}

function handleRouteUpdate(options = {}) {
  const target =
    options.target || (typeof window !== "undefined" ? window : undefined)
  if (!target) return false
  const registry = options.registry || MARKETING_CAMPAIGNS
  const now = options.now === undefined ? Date.now() : options.now
  const storage =
    options.sessionStorage === undefined
      ? getBrowserSessionStorage(target)
      : options.sessionStorage
  const incomingSearch = target.location ? target.location.search : ""
  const urlCleanup = replaceTrackingUrl(target, {
    navigate: options.navigate,
  })
  if (!urlCleanup.cleaned) {
    target.__KALPILLI_MEASUREMENT_EPHEMERAL_DISABLED__ = true
    target.__KALPILLI_MEASUREMENT_URL_CLEANUP_FAILED__ = true
  }
  const snapshot = getCurrentMeasurementSnapshot(target, options.localStorage)
  const enabled = isMeasurementEnabled(snapshot.state)
  target.__KALPILLI_MEASUREMENT_STATE__ = snapshot.state
  const attribution = processCampaignAttribution({
    search: incomingSearch,
    storage,
    registry,
    now,
    measurementEnabled: enabled,
  })
  if (!enabled) {
    denyFutureMeasurement(target)
    target.__KALPILLI_MEASUREMENT_STATE__ = snapshot.state
    return false
  }
  if (urlCleanup.routeUpdateScheduled) return false
  return dispatchPageView(
    target,
    options.location || target.location,
    options.title || "",
    attribution
  )
}

function changeMeasurementPreference(preference, options = {}) {
  const target =
    options.target || (typeof window !== "undefined" ? window : undefined)
  if (!target) {
    return {
      persisted: false,
      reloadScheduled: false,
      limitation: "unavailable",
    }
  }
  if (target[PREFERENCE_CHANGE_MARKER]?.result) {
    return target[PREFERENCE_CHANGE_MARKER].result
  }
  if (target[RELOAD_MARKER]) {
    return { persisted: false, reloadScheduled: true, limitation: null }
  }
  if (target[PREFERENCE_CHANGE_MARKER]) {
    return {
      persisted: false,
      reloadScheduled: false,
      limitation: "in_flight",
    }
  }

  const operation = { result: null }
  target[PREFERENCE_CHANGE_MARKER] = operation
  const document = options.document || target.document
  const localStorage = options.localStorage
  const sessionStorage =
    options.sessionStorage === undefined
      ? getBrowserSessionStorage(target)
      : options.sessionStorage
  const snapshot = getMeasurementSnapshot(target, localStorage)
  if (snapshot.dntActive) {
    operation.result = {
      persisted: false,
      reloadScheduled: false,
      limitation: "dnt",
    }
    delete target[PREFERENCE_CHANGE_MARKER]
    return operation.result
  }
  const persisted = storeMeasurementPreference(preference, snapshot.storage)
  if (preference === "disabled") {
    denyFutureMeasurement(target)
    removeStoredAttribution(sessionStorage)
    deleteKnownGaCookies(document, target.location && target.location.hostname)
  }
  if (!persisted) {
    if (preference === "disabled") {
      target.__KALPILLI_MEASUREMENT_EPHEMERAL_DISABLED__ = true
      target.__KALPILLI_MEASUREMENT_STATE__ = "disabled"
    }
    operation.result = {
      persisted: false,
      reloadScheduled: false,
      limitation: "storage_blocked",
    }
    return operation.result
  }
  operation.result = {
    persisted: true,
    reloadScheduled: true,
    limitation: null,
  }
  if (!scheduleReloadOnce(target)) {
    operation.result.reloadScheduled = false
    delete target[PREFERENCE_CHANGE_MARKER]
  }
  return operation.result
}

function readCurrentAttribution(target, options = {}) {
  const storage =
    options.sessionStorage === undefined
      ? getBrowserSessionStorage(target)
      : options.sessionStorage
  return readStoredAttribution(
    storage,
    options.now === undefined ? Date.now() : options.now,
    options.registry || MARKETING_CAMPAIGNS
  )
}

module.exports = {
  MEASUREMENT_STATE_EVENT,
  bindMeasurementStorageSync,
  changeMeasurementPreference,
  getCurrentMeasurementSnapshot,
  handleMeasurementStorageEvent,
  handleRouteUpdate,
  initializeMeasurement,
  initializeProviders,
  readCurrentAttribution,
}
